if (!customElements.get('premium-testimonials')) {
  class PremiumTestimonials extends HTMLElement {
    connectedCallback() {
      this.slides        = Array.from(this.querySelectorAll('.lumina-testimonial__slide'));
      this.dots          = Array.from(this.querySelectorAll('.lumina-testimonial__dot'));
      this.sidebarItems  = Array.from(this.querySelectorAll('.lumina-testimonial__sidebar-item'));
      this.prevBtn       = this.querySelector('.lumina-testimonial__nav-btn--prev');
      this.nextBtn       = this.querySelector('.lumina-testimonial__nav-btn--next');
      this.progressFill  = this.querySelector('.lumina-testimonial__progress-fill');
      this.canvas        = this.querySelector('.lumina-testimonial__particles');

      this.current         = 0;
      this.total           = this.slides.length;
      this.intervalMs      = 6500;
      this.isTransitioning = false;
      this.isPaused        = false;
      this.progressStart   = null;
      this.progressPaused  = 0;   // elapsed ms when paused
      this.rafId           = null;

      if (!this.total) return;

      this._initParticles();
      this._bindEvents();
      this._startProgress();
    }

    disconnectedCallback() {
      cancelAnimationFrame(this.rafId);
    }

    /* ── Event binding ────────────────────────────────── */
    _bindEvents() {
      this.prevBtn?.addEventListener('click', () => this._navigate(-1));
      this.nextBtn?.addEventListener('click', () => this._navigate(1));
      this.dots.forEach((dot, i) => dot.addEventListener('click', () => this._goTo(i)));
      this.sidebarItems.forEach((item, i) => item.addEventListener('click', () => this._goTo(i)));

      this.addEventListener('mouseenter', () => this._pause());
      this.addEventListener('mouseleave', () => this._resume());
      this.addEventListener('focusin',    () => this._pause());
      this.addEventListener('focusout',   () => this._resume());
    }

    /* ── Navigation ──────────────────────────────────── */
    _navigate(dir) {
      this._goTo((this.current + dir + this.total) % this.total);
    }

    _goTo(index) {
      if (index === this.current || this.isTransitioning) return;
      this.isTransitioning = true;

      const prevIndex = this.current;
      this.current    = index;

      // Animate OUT the leaving slide
      const leaving = this.slides[prevIndex];
      leaving.classList.add('is-leaving');
      leaving.classList.remove('is-active');
      leaving.setAttribute('aria-hidden', 'true');

      const onLeaveEnd = () => {
        leaving.classList.remove('is-leaving');
        leaving.removeEventListener('animationend', onLeaveEnd);
        this.isTransitioning = false;
      };
      leaving.addEventListener('animationend', onLeaveEnd);
      // Fallback: clear transitioning flag after max leave duration
      setTimeout(() => { leaving.classList.remove('is-leaving'); this.isTransitioning = false; }, 500);

      // Animate IN the incoming slide
      const entering = this.slides[index];
      entering.classList.add('is-active');
      entering.removeAttribute('aria-hidden');

      // Update indicators
      this.dots.forEach((d, i) => {
        d.classList.toggle('is-active', i === index);
        d.setAttribute('aria-selected', String(i === index));
      });
      this.sidebarItems.forEach((s, i) => s.classList.toggle('is-active', i === index));

      // Restart progress
      this._startProgress();
    }

    /* ── Progress Bar (rAF-driven) ───────────────────── */
    _startProgress() {
      cancelAnimationFrame(this.rafId);
      this.progressStart  = null;
      this.progressPaused = 0;
      if (this.progressFill) this.progressFill.style.width = '0%';
      if (!this.isPaused) this._animateProgress();
    }

    _animateProgress() {
      const tick = (timestamp) => {
        if (!this.progressStart) this.progressStart = timestamp;
        const elapsed = timestamp - this.progressStart + this.progressPaused;
        const pct     = Math.min((elapsed / this.intervalMs) * 100, 100);

        if (this.progressFill) this.progressFill.style.width = pct + '%';

        if (pct < 100) {
          this.rafId = requestAnimationFrame(tick);
        } else {
          this._navigate(1);
        }
      };
      this.rafId = requestAnimationFrame(tick);
    }

    _pause() {
      if (this.isPaused) return;
      this.isPaused = true;

      cancelAnimationFrame(this.rafId);
      // Snapshot how much time has elapsed
      if (this.progressStart !== null) {
        this.progressPaused += performance.now() - this.progressStart;
      }
      this.progressStart = null;
    }

    _resume() {
      if (!this.isPaused) return;
      this.isPaused  = false;
      this.progressStart = null;
      this._animateProgress();
    }

    /* ── Floating Particle Canvas ────────────────────── */
    _initParticles() {
      if (!this.canvas || typeof this.canvas.getContext !== 'function') return;

      const ctx     = this.canvas.getContext('2d');
      const COUNT   = 42;
      const rand    = (a, b) => Math.random() * (b - a) + a;
      const GOLDEN  = 'rgba(212,188,123,';
      const WHITE   = 'rgba(255,255,255,';

      // Build particles with normalised (0-1) positions
      const particles = Array.from({ length: COUNT }, () => ({
        x:       rand(0, 1),
        y:       rand(0, 1),
        r:       rand(0.6, 2.4),
        speed:   rand(0.00006, 0.00022),
        opacity: rand(0.06, 0.38),
        phase:   rand(0, Math.PI * 2),
        color:   Math.random() > 0.45 ? GOLDEN : WHITE,
      }));

      let w = 0, h = 0, lastTime = 0;

      const resize = () => {
        w = this.canvas.width  = this.canvas.offsetWidth  || this.offsetWidth;
        h = this.canvas.height = this.canvas.offsetHeight || this.offsetHeight;
      };

      const draw = (time) => {
        const dt = time - lastTime;
        lastTime = time;

        // Lazy-resize (only when dimensions actually change)
        const cw = this.canvas.offsetWidth;
        const ch = this.canvas.offsetHeight;
        if (cw !== w || ch !== h) resize();

        ctx.clearRect(0, 0, w, h);

        for (const p of particles) {
          p.phase += dt * p.speed * Math.PI * 2;

          const alpha = p.opacity * (0.5 + 0.5 * Math.sin(p.phase));
          const px    = (p.x + Math.sin(p.phase * 0.65) * 0.025) * w;
          const py    = (p.y + Math.cos(p.phase * 0.48) * 0.025) * h;

          ctx.beginPath();
          ctx.arc(px, py, p.r, 0, Math.PI * 2);
          ctx.fillStyle = p.color + alpha.toFixed(3) + ')';
          ctx.fill();
        }

        requestAnimationFrame(draw);
      };

      resize();
      requestAnimationFrame(draw);
    }
  }

  customElements.define('premium-testimonials', PremiumTestimonials);
}